import { inject, Injectable, Service } from '@angular/core';
import { VisibilityTypeProductsListEnum } from 'app/shared/enums/visibility-type-products-list.enum.model';
import { EntityChild } from 'app/shared/models/entity-child.model';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { PagedResponse } from 'app/shared/models/paged-response.model';
import { styleGroupDTO } from 'app/shared/models/style-group.model';
import { BasicHttpClientService } from 'app/shared/services/basic-http-client.service';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { environment } from 'environments/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IncludeModeEnum } from '../../enum/include-mode-type.enum';
import { ProductVisibilityTypeEnum } from '../../enum/products-visibility-type.enum.model';
import { VisibilityMediaType } from '../../enum/visibility-media-type.enum';
import { VariantBulkEditorDto } from '../../models/back-office-admin-common/variant-bulk-editor-dto.model';
import { MediaDTO, OrderedMediaDTO } from '../../../../shared/models/media.models';
import { PaletteDTO, PaletteVM } from '../../models/common/palette.models';
import { PrimaryColorDTO } from '../../models/common/primary-color.models';
import { AssortmentRuleDTO } from '../../models/products/assortment-condition.models';
import { ReorderTagUpdateDto } from '../../models/settings/sales-campaigns.model';
import { AssortmentProductOrderDto } from '../../models/products/assortment-poduct-order.model';
import { AssortmentBulkOrderRequestDto, AssortmentDTO, AssortmentItemVM, AssortmentVM } from '../../models/products/assortment.model';
import { BulkEditorVariantDTI, BulkEditorVariantDTO, BulkEditorVariantVM } from '../../models/products/bulk-editor-variant.model';
import { ImportProductJob, JobDetailsDto, JobDetailsVm, jobImport, ProductToImportBase, ProductToImportVM } from '../../models/products/product-to-import.model';
import {
  AssortmentProductDTO,
  ProductForCollapseVM,
  CategoryDTO,
  OrderedMediaResDTO,
  ProductDTO,
  ProductItemVM,
  ProductTagDTO,
  ProductVariantVM,
  ProductVM,
  SeasonDTO,
  SizeDTO,
} from '../../models/products/product.models';
import {
  ProductPaletteLimitDTO,
  StockLimitProductPaletteVM,
  limitDTI,
} from '../../models/products/stock-product-pallettes.models';
import { VariantFullDTO, VariantPaletteDTO, VariantPricelistDTO, VariantVM } from '../../models/products/variant.models';
import { ProductTier } from '../../models/settings/tier.model';
import { UserDTO } from '../../models/users/user.model';
import { MediagMapper } from '../../utilities/media-mapper';
import { ProductsMapper } from '../../utilities/products-mapper';
import { Params } from '@angular/router';
import { ProductOverview } from 'app/shared/models/product-list.model';
import { OrderTypeEnum } from 'app/shared/enums/order-type.enum.model';
import { ProductPriceDetails } from 'app/shop/shared/models/product-price-details.model';
import { ShopContextStore } from 'app/shared/state/shop-context.store';
import { ProductCard } from 'app/shop/shared/models/product-card.model';
import { Order } from 'app/shop/shared/models/order.model';

export class ProductToImportDTO extends ProductToImportBase {
  public styleCode: string;
  public styleName: string;
  public modelId: number;
}

/** Oggetto di configurazione per i parametri di getProducts */
export interface GetProductsParams {
  skipCount: number;
  maxResultCount: number;
  search: string;
  includeVariants: boolean;
  includeMedia: boolean;
  includeVariantMedia: boolean;
  assortmentId: number;
  includeVariantDiscounts?: boolean;
  wholeSalePriceFrom?: number;
  wholeSalePriceTo?: number;
  retailPriceFrom?: number;
  retailPriceTo?: number;
  visibilityReOrder?: number;
  visibilityPreOrder?: number;
  externalCollectionIds?: string[];
  sorting?: string;
  styleGroupId?: number;
  channelId?: number;
  variantsWithTierIds?: number[];
  variantsWithoutTierIds?: number[];
  priceListType?: OrderTypeEnum;
  includeTag?: boolean;
}

export interface GetPagedSearchParams {
  loaderId?: string;
  skipCount: number;
  maxResultCount: number;
  search?: string;
}

export interface GetCustomizableProductListParams {
  loaderId?: string;
  skipCount: number;
  maxResultCount: number;
  searchString: string;
  channelId: number;
  posId: number;
}

export interface GetProductsToImportParams {
  loaderId?: string;
  storeId: number;
  paginationPage: number;
  paginationSize: number;
  search?: string;
  collectionId?: number;
}

export interface GetProductsListParams {
  loaderId: string;
  skipCount: number;
  maxResultCount: number;
  search: string;
  wholeSalePriceFrom?: number;
  wholeSalePriceTo?: number;
  retailPriceFrom?: number;
  retailPriceTo?: number;
  visibilityReOrder?: number;
  visibilityPreOrder?: number;
  assortmentId?: number;
  externalCollectionIds?: string[];
  requestResolution?: number;
  sorting?: string;
  styleGroupId?: number;
}

export interface GetVariantsParams {
  loaderId?: string;
  skipCount?: number;
  maxResultCount?: number;
  productId?: number;
  assortmentId?: number;
  includeMedia?: boolean;
  searchString?: string;
  externalCollectionIds?: string[];
  eanRequired?: boolean;
  customerId?: number;
  channelId?: number;
  priceListType?: OrderTypeEnum;
  visibleForPreOrder?: boolean;
  visibleForReOrder?: boolean;
}

export interface GetAssortmentsParams {
  loaderId?: string;
  skipCount: number;
  maxResultCount: number;
  search: string;
  visibilityType?: ProductVisibilityTypeEnum;
  includeMedia?: boolean;
  sorting?: string;
  channelId?: number;
  showcaseId?: number;
}

@Service()
export class ProductsService {
  private _httpClient = inject(BasicHttpClientService);

  private _integrationBaseUrl: string;
  private _baseUrl: string;

  private _shopContext = inject(ShopContextStore);

  constructor(
  ) {
    this._integrationBaseUrl = environment.urls.integrationApiUrl.url;
    this._baseUrl = environment.urls.apiUrl.url;
  }

  public getProductsToImport(query: GetProductsToImportParams): Observable<PagedResponse<ProductToImportVM>> {
    const { storeId, paginationPage, paginationSize, search, collectionId, loaderId } = query;
    const params = {
      storeId: storeId.toString(),
      paginationPage: paginationPage.toString(),
      paginationSize: paginationSize.toString(),
      collectionId: collectionId ? collectionId.toString() : '',
      search: search || ''
    };
    const url = this._integrationBaseUrl + '/job-detail/basic-product-job-detail';
    const subj = new Subject<PagedResponse<ProductToImportVM>>();

    this._httpClient.get<PagedResponse<ProductToImportDTO>>(url, {
      params
    }, loaderId).subscribe(data => {
      subj.next(new PagedResponse<ProductToImportVM>({
        items: data.items.map(x => ProductsMapper.ProductToImportVM(x, this._shopContext.currentShop()?.discount)),
        totalCount: data.totalCount
      }));
    });
    return subj.asObservable();
  }

  public importProducts(loaderId: string = null, storeId: number, externalIds: string[], wholesalePercentageDiscount: number,): Observable<any> {
    const body = new jobImport({
      wholesalePercentageDiscount: wholesalePercentageDiscount,
      importProductJobs: externalIds.map(x => {
        return new ImportProductJob({
          externalId: x,
          storeId,
        });
      }),
    });

    return this._httpClient.post<any>(`${this._integrationBaseUrl}/job/job-basic`, body, null, loaderId);
  }

  public importProductsMultiple(loaderId: string = null, storeId: number, products: { id: number, wholesalePercentageDiscount: number; }[]): Observable<any> {
    const data: { productIds: number[], wholesalePercentageDiscount: number; }[] = [];
    (products || []).forEach(p => {
      const ex = data.find(x => x.wholesalePercentageDiscount === p.wholesalePercentageDiscount);
      if (ex) ex.productIds.push(p.id);
      else data.push({ productIds: [p.id], wholesalePercentageDiscount: p.wholesalePercentageDiscount });
    });
    const requests: HttpBasicRequest[] = data.map(r => new HttpBasicRequest({
      body: new jobImport({
        wholesalePercentageDiscount: r.wholesalePercentageDiscount,
        importProductJobs: r.productIds.map(id => new ImportProductJob({ externalId: id.toString(), storeId })),
      }),
      method: 'post',
      url: `${this._integrationBaseUrl}/job/job-basic`
    }));
    return this._httpClient.forkJoin<any>(requests, loaderId);
  }

    public getProductsList(query: GetProductsListParams): Observable<PagedResponse<ProductItemVM>> {
    const {
      loaderId, skipCount, maxResultCount, search, wholeSalePriceFrom, wholeSalePriceTo, retailPriceFrom,
      retailPriceTo, visibilityReOrder, visibilityPreOrder, assortmentId, externalCollectionIds,
      requestResolution = 60, sorting, styleGroupId
    } = query;
    const filters: GetProductsParams = {
      skipCount,
      maxResultCount,
      search,
      includeVariants: false,
      includeMedia: true,
      includeVariantMedia: false,
      assortmentId,
      includeVariantDiscounts: false,
      wholeSalePriceFrom,
      wholeSalePriceTo,
      retailPriceFrom,
      retailPriceTo,
      visibilityReOrder,
      visibilityPreOrder,
      externalCollectionIds,
      sorting,
      styleGroupId,
      channelId: OrderTypeEnum.PreOrder as any, // valore preservato dalla chiamata originale, vedi nota sotto
      priceListType: null,
      includeTag: true,
    };
    return this.getProducts(loaderId, filters).pipe(
      map(data => new PagedResponse<ProductItemVM>({
        items: data.items.map(x => ProductsMapper.ProductItemVM(x, requestResolution)),
        totalCount: data.totalCount
      }))
    );
  }

  public getProducts(
    loaderId: string,
    filters: GetProductsParams,
  ): Observable<PagedResponse<ProductDTO>> {
    const {
      skipCount,
      maxResultCount,
      search,
      includeVariants,
      includeMedia,
      includeVariantMedia,
      assortmentId,
      includeVariantDiscounts = false,
      wholeSalePriceFrom,
      wholeSalePriceTo,
      retailPriceFrom,
      retailPriceTo,
      visibilityReOrder,
      visibilityPreOrder,
      externalCollectionIds,
      sorting,
      styleGroupId,
      channelId,
      variantsWithTierIds,
      variantsWithoutTierIds,
      priceListType,
      includeTag = false,
    } = filters;

    const storeId = this._shopContext.currentShop()?.id;
    const params: any = {
      storeId: storeId.toString(),
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      includeVariants,
      visibilityMedia: includeMedia ? VisibilityMediaType.ShowAllMedia : VisibilityMediaType.NoShowMedia,
      includeVariantMedia,
      searchString: search || '',
    };
    if (includeTag?.toString()) params['includeTag'] = includeTag;
    if (wholeSalePriceFrom?.toString()) params['wholeSalePriceFrom'] = wholeSalePriceFrom;
    if (wholeSalePriceTo?.toString()) params['wholeSalePriceTo'] = wholeSalePriceTo;
    if (retailPriceFrom?.toString()) params['retailPriceFrom'] = retailPriceFrom;
    if (retailPriceTo?.toString()) params['retailPriceTo'] = retailPriceTo;
    if (visibilityReOrder?.toString()) params['visibilityReOrder'] = visibilityReOrder;
    if (visibilityPreOrder?.toString()) params['visibilityPreOrder'] = visibilityPreOrder;
    if (externalCollectionIds?.length) params['externalCollectionIds'] = externalCollectionIds;
    if (includeVariantDiscounts) params['includeVariantDiscounts'] = includeVariantDiscounts;
    if (styleGroupId?.toString()) params['styleGroupId'] = styleGroupId;
    if (sorting) params['sorting'] = sorting;
    if (channelId) params['channelId'] = channelId;
    if (assortmentId) params['assortmentId'] = assortmentId;
    if (variantsWithTierIds) params['variantsWithTierIds'] = variantsWithTierIds;
    if (variantsWithoutTierIds) params['variantsWithoutTierIds'] = variantsWithoutTierIds;
    if (priceListType != undefined && priceListType != null) params['priceListType'] = priceListType;

    const url = this._baseUrl + '/products';
    return this._httpClient.get<PagedResponse<ProductDTO>>(url, {
      params
    }, loaderId);
  }

  public getProductsToIncludeInAssortment(
    assortmentId: number,
    skipCount: number,
    maxResultCount: number,
    search?: string,
    assortmentIdFilter?: number,
    loaderId?: string,
    includeVariantDiscounts: boolean = false,
    wholeSalePriceFrom?: number,
    wholeSalePriceTo?: number,
    retailPriceFrom?: number,
    retailPriceTo?: number,
    visibilityReOrder?: number,
    visibilityPreOrder?: number,
    externalCollectionIds?: string[],
    requestResolution: number = 60,
    disableCheck = false,
    styleGroupId?: number,
    channelId?: number,
  ): Observable<PagedResponse<ProductForCollapseVM>> {
    const _sub = new Subject<PagedResponse<ProductForCollapseVM>>();
    const filters: GetProductsParams = {
      skipCount,
      maxResultCount,
      search,
      includeVariants: true,
      includeMedia: true,
      includeVariantMedia: true,
      assortmentId: assortmentIdFilter,
      includeVariantDiscounts,
      wholeSalePriceFrom,
      wholeSalePriceTo,
      retailPriceFrom,
      retailPriceTo,
      visibilityReOrder,
      visibilityPreOrder,
      externalCollectionIds,
      sorting: null,
      styleGroupId,
      channelId,
    };
    this.getProducts(loaderId, filters).subscribe(data => {
      var result = new PagedResponse<ProductForCollapseVM>({
        items: data.items.map(x => ProductsMapper.AssortmentProductVM(x, null, requestResolution, { preOrder: visibilityPreOrder, reOrder: visibilityReOrder })),
        totalCount: data.totalCount
      });
      if (!!assortmentId && !!data?.items?.length) {
        const prodIdsRequests: HttpBasicRequest[] = data.items.map(x => new HttpBasicRequest({
          method: 'get',
          options: { params: { assortmentId, productId: x.id, storeId: this._shopContext.currentShop().id, maxResultCount: 0, skipCount: 0 } },
          url: this._baseUrl + '/variants',
          parentId: x.id
        }));
        this._httpClient.forkJoin(prodIdsRequests, loaderId).subscribe((data: PagedResponse<VariantFullDTO>[]) => {
          data.forEach((varsData, index) => {
            const el = result.items[index];
            el.includedQty = 0;
            el.variants.forEach(vr => {
              vr.checked = varsData?.items?.some(v => v.id === vr.id) && !disableCheck;
              if (vr.checked) el.includedQty += 1;
            });
            el.checked = el.includedQty === el.variants.length;
          });
          _sub.next(result);
        });
      } else {
        _sub.next(result);
      }
    });
    return _sub.asObservable();
  }

  public getProductsAndVariant(
    skipCount: number,
    maxResultCount: number,
    search?: string,
    assortmentId?: number,
    loaderId?: string,
    includeVariantDiscounts: boolean = false,
    wholeSalePriceFrom?: number,
    wholeSalePriceTo?: number,
    retailPriceFrom?: number,
    retailPriceTo?: number,
    visibilityReOrder?: VisibilityTypeProductsListEnum,
    visibilityPreOrder?: VisibilityTypeProductsListEnum,
    externalCollectionIds?: string[],
    requestResolution: number = 60,
    sorting?: string,
    styleGroupId?: number,
    variantsWithTierIds?: number[],
    variantsWithoutTierIds?: number[],
  ): Observable<PagedResponse<ProductForCollapseVM>> {
    const filters: GetProductsParams = {
      skipCount,
      maxResultCount,
      search,
      includeVariants: true,
      includeMedia: true,
      includeVariantMedia: true,
      assortmentId,
      includeVariantDiscounts,
      wholeSalePriceFrom,
      wholeSalePriceTo,
      retailPriceFrom,
      retailPriceTo,
      visibilityReOrder,
      visibilityPreOrder,
      externalCollectionIds,
      sorting,
      styleGroupId,
      channelId: null,
      variantsWithTierIds,
      variantsWithoutTierIds,
    };
    return this.getProducts(loaderId, filters).pipe(map(data => new PagedResponse<ProductForCollapseVM>({
      items: data.items.map(x => ProductsMapper.AssortmentProductVM(x, null, requestResolution, { preOrder: visibilityPreOrder, reOrder: visibilityReOrder })),
      totalCount: data.totalCount
    })));
  }

  public getAssortmentProducts(
    assortmentId: number,
    visibilityType?: ProductVisibilityTypeEnum,
    loaderId?: string,
    requestResolution: number = 60,
  ): Observable<ProductForCollapseVM[]> {
    return this._httpClient.get<AssortmentProductDTO[]>(
      this._baseUrl + '/assortments/' + assortmentId.toString() + '/products',
      { params: { visibilityType: visibilityType !== null && visibilityType != undefined ? visibilityType : null } },
      loaderId
    ).pipe(map(data => data.map(p => ProductsMapper.AssortmentProductVM(p.product, p.order, requestResolution))));
  }

  public getVariant(loaderId: string, variantId: number): Observable<VariantVM> {
    const _data = new Subject<VariantVM>();
    this._httpClient.get<VariantFullDTO>(this._baseUrl + '/variants/' + variantId.toString(), null, loaderId).subscribe(data => {
      const variant: VariantFullDTO = data;
      const requests: HttpBasicRequest[] = [
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/prices', method: 'get' },
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/tags', method: 'get' },
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/labels', method: 'get' },
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/categories', method: 'get' },
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/purposes', method: 'get' },
        { url: this._baseUrl + '/variants/' + variantId.toString() + '/main-media-by-resolution', method: 'get' }
      ];
      if (variant.teamId) requests.push({ url: this._baseUrl + '/teams/' + variant.teamId, method: 'get' });
      this._httpClient.forkJoin<any>(requests, loaderId).subscribe(data => {
        const pricelists: VariantPricelistDTO[] = data[0];
        const tags: ProductTagDTO[] = data[1];
        const labels: EntityChild[] = data[2];
        const categories: EntityChild[] = data[3];
        const purposes: EntityChild[] = data[4];
        const media: OrderedMediaDTO[] = data[5];
        const teams = data[6] ? [data[6]] : [];
        _data.next(ProductsMapper.VariantVM(variant, media, pricelists, tags, labels, categories, purposes, teams));
      });
    });
    return _data.asObservable();
  }

  public getProduct(loaderId: string, productId: number): Observable<ProductVM> {
    const _sub = new Subject<ProductVM>();
    const storeId = this._shopContext.currentShop().id;
    this._httpClient.forkJoin<any>(
      [
        { url: this._baseUrl + `/products/${productId}`, method: 'get' },
        { url: this._baseUrl + `/products/${productId}/media`, method: 'get' },
      ], loaderId
    ).subscribe((data) => {

      const prod: ProductDTO = data[0];
      const media: OrderedMediaResDTO[] = data[1];
      _sub.next(ProductsMapper.ProductVM(prod, media));
      _sub.complete();
    });
    return _sub.asObservable();
  }

  public getVariants(query: GetVariantsParams = {}): Observable<PagedResponse<ProductVariantVM>> {
    const {
      loaderId, skipCount = null, maxResultCount = null, productId = null, assortmentId = null,
      includeMedia = false, searchString = null, externalCollectionIds, eanRequired = false,
      customerId, channelId, priceListType, visibleForPreOrder, visibleForReOrder
    } = query;
    const storeId = this._shopContext.currentShop().id;
    const url = this._baseUrl + '/variants';
    const params = {
      storeId,
      visibilityMedia: includeMedia ? VisibilityMediaType.ShowAllMedia : VisibilityMediaType.NoShowMedia
    };
    if (skipCount >= 0 && skipCount !== null) params['skipCount'] = skipCount;
    if (maxResultCount >= 0 && maxResultCount !== null) params['maxResultCount'] = maxResultCount;
    if (productId) params['productId'] = productId;
    if (assortmentId) params['assortmentId'] = assortmentId;
    if (searchString) params['searchString'] = searchString;
    if (!!externalCollectionIds?.length) params['externalCollectionIds'] = externalCollectionIds;
    if (eanRequired) params['eanRequired'] = eanRequired;
    if (customerId) params['customerId'] = customerId;
    if (channelId) params['channelId'] = channelId;
    if (priceListType != null) params['priceListType'] = priceListType;
    if (visibleForPreOrder != null) params['visibleForPreOrder'] = visibleForPreOrder;
    if (visibleForReOrder != null) params['visibleForReOrder'] = visibleForReOrder;

    return this._httpClient.get<PagedResponse<VariantFullDTO>>(url, {
      params
    }, loaderId)
      .pipe(map(data => {
        return new PagedResponse({
          items: data.items.map(x => ProductsMapper.ProductVariantVM(x)),
          totalCount: data.totalCount
        });
      }));
  }

  public getAssortments(query: GetAssortmentsParams): Observable<PagedResponse<AssortmentItemVM>> {
    const { skipCount, maxResultCount, search, visibilityType, loaderId, includeMedia, sorting, channelId, showcaseId } = query;
    const params = {
      storeId: this._shopContext.currentShop()?.id,
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: search || '',
      visibilityMedia: includeMedia ? VisibilityMediaType.ShowAllMedia : VisibilityMediaType.NoShowMedia,
      sorting: sorting || 'title',
    };
    if (channelId?.toString()) params['channelId'] = channelId.toString();
    if (visibilityType?.toString()) params['visibilityType'] = visibilityType.toString();
    if (showcaseId) params['showcaseId'] = showcaseId.toString();

    const url = this._baseUrl + '/assortments';
    return this._httpClient.get<PagedResponse<AssortmentDTO>>(url, {
      params
    }, loaderId).pipe(map(data => new PagedResponse<AssortmentItemVM>({
      items: data.items.map(x => ProductsMapper.AssortmentItemVM(x, this._shopContext.activatedChannels())),
      totalCount: data.totalCount
    })));
  }

  public getAssortment(assortmentId: number, loaderId?: string): Observable<AssortmentVM> {
    return this._httpClient.forkJoin<any>(
      [
        { url: this._baseUrl + '/assortments/' + assortmentId.toString(), method: 'get' },
        { url: this._baseUrl + '/assortments/' + assortmentId.toString() + '/tags', method: 'get' },
        { url: this._baseUrl + '/assortments/' + assortmentId.toString() + '/tiers', method: 'get' },
        { url: this._baseUrl + '/assortments/' + assortmentId.toString() + '/media', method: 'get' },
      ], loaderId).pipe(
        map(data => {
          const ass: AssortmentDTO = data[0];
          const tags: ProductTagDTO[] = data[1];
          const assTiers: ProductTier[] = data[2];
          const media: OrderedMediaDTO[] = data[3];
          const result = ProductsMapper.AssortmentVM(ass, assTiers, tags, media, this._shopContext.activatedChannels());
          return result;
        })
      );
  }

  public getAssortmentRules(loaderId: string, assortmentId: number): Observable<AssortmentRuleDTO[]> {
    const url = this._baseUrl + '/assortments/' + assortmentId.toString() + '/assortment-rules';
    return this._httpClient.get<AssortmentRuleDTO[]>(url, {}, loaderId);
  }

  public saveAssortmentProductOrder(loaderId: string, assortmentId: number, productsOrders: AssortmentProductOrderDto[]): Observable<any> {
    return this._httpClient.put(this._baseUrl + `/assortments/${assortmentId}/product-list`, productsOrders, {}, loaderId);
  }

  public getColors(loaderId: string): Observable<PagedResponse<PrimaryColorDTO>> {
    const url = this._baseUrl + '/primary-colors';
    return this._httpClient.get<PagedResponse<PrimaryColorDTO>>(url, { params: { maxResultCount: 1000 } }, loaderId);
  }

  getPalettes(loaderId: string, page: number, searchString: string): Observable<PagedResponse<PaletteDTO>> {
    const skipCount = page * 20;
    return this._httpClient.get<PagedResponse<PaletteDTO>>(this._baseUrl + '/palettes', { params: { skipCount, maxResultCount: 20, storeId: this._shopContext.currentShop().id, searchString } }, loaderId);
  }

  getPaletteById(loaderId: string, paletteId: string) {
    return this._httpClient.get<PaletteDTO>(this._baseUrl + `/palettes/${paletteId}`, null, loaderId);
  }

  public saveUnlimitedReorderVariantChanges(
    loaderId: string,
    tagName: string,
    variantIdsToAdd: number[],
    variantIdsToRemove: number[],
  ): Observable<void> {
    const body: ReorderTagUpdateDto = {
      tagName,
      variantIdsToAdd: variantIdsToAdd ?? [],
      variantIdsToRemove: variantIdsToRemove ?? [],
    };
    return this._httpClient.post<void>(
      `${this._baseUrl}/variants/reorder-tags`,
      body,
      null,
      loaderId,
    );
  }

  public associateVariantTag(loaderId: string, variantId: number, tagId: number) {
    const url = `${this._baseUrl}/variants/${variantId}/tag/${tagId}`;
    return this._httpClient.post(url, null, {}, loaderId);
  }

  public deleteAssociationVariantTag(loaderId: string, variantId: number, tagId: number) {
    const url = `${this._baseUrl}/variants/${variantId}/tag/${tagId}`;
    return this._httpClient.delete(url, {}, loaderId);
  }

  public getCategories(loaderId: string): Observable<PagedResponse<CategoryDTO>> {
    const url = this._baseUrl + '/categories';
    return this._httpClient.get<PagedResponse<CategoryDTO>>(url, { params: { maxResultCount: 0 } }, loaderId);
  }

  public getSeasons(loaderId: string): Observable<PagedResponse<SeasonDTO>> {
    const url = this._baseUrl + '/seasons';
    return this._httpClient.get<PagedResponse<SeasonDTO>>(url, { params: { maxResultCount: 0 } }, loaderId);
  }

  public getStyleGroup(query: GetPagedSearchParams): Observable<PagedResponse<styleGroupDTO>> {
    const { loaderId, skipCount, maxResultCount, search } = query;
    const params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: search || '',
      sorting: 'name asc',
      storeId: this._shopContext.currentShop()?.id
    };
    return this._httpClient.get<PagedResponse<styleGroupDTO>>(`${this._baseUrl}/style-group`, { params }, loaderId);
  }

  saveProduct(loaderId: string, product: ProductVM, cacheProduct: ProductVM, mediaFiles: File[], basicKustomPalette: PaletteVM, cacheBasicKustomPalette: PaletteVM) {
    const result = new Subject();
    let requests: HttpBasicRequest[] = [];

    requests = requests.concat(ProductsMapper.tagAssociateRequest(product.tags, cacheProduct.tags, product.id, this._baseUrl, 'products'));
    requests = requests.concat(ProductsMapper.tagDeleteRequest(product.tags, cacheProduct.tags, product.id, this._baseUrl, 'products'));
    requests = requests.concat(ProductsMapper.basicKustomPaletteSetRequest(basicKustomPalette, cacheBasicKustomPalette, product.id, this._baseUrl));
    requests = requests.concat(ProductsMapper.basicKustomPaletteUnsetRequest(basicKustomPalette, cacheBasicKustomPalette, product.id, this._baseUrl));

    const mediaUploadRequest: HttpBasicRequest[] = MediagMapper.uploadMedia(mediaFiles, this._baseUrl);

    let mediaUpdateRequest: HttpBasicRequest[] = [];

    new Promise((resolve) => {
      this._httpClient.put(this._baseUrl + '/products/' + product.id, product, null, loaderId).subscribe(() => {
        if (mediaUploadRequest.length > 0) {
          this._httpClient.forkJoin(mediaUploadRequest, loaderId).subscribe((res: MediaDTO[]) => {
            mediaUpdateRequest = MediagMapper.updateAssociatedMedia(res, product.media, cacheProduct.media, this._baseUrl, product.id, 'products');
            resolve(mediaUpdateRequest);
          }, error => {
            console.error(error);
          });
        } else {
          mediaUpdateRequest = MediagMapper.updateAssociatedMedia(null, product.media, cacheProduct.media, this._baseUrl, product.id, 'products');
          resolve(mediaUpdateRequest);
        }
      });
    }).then(() => {
      requests = requests.concat(mediaUpdateRequest);
      if (requests.length === 0) {
        result.next(null);
      } else {
        this._httpClient.forkJoin(requests, loaderId).subscribe((res) => {
          result.next(res[0]);
        }, error => {
          console.error(error);
        });
      }
    });

    return result;
  }

  updateAssortment(assortment: AssortmentItemVM, loaderId: string){
    return this._httpClient.put(`${this._baseUrl}/assortments/${assortment.id}`, assortment , {} , loaderId)
  }

  bulkUpdateAssortmentOrder(loaderId: string, dto: AssortmentBulkOrderRequestDto): Observable<void> {
    return this._httpClient.put<void>(`${this._baseUrl}/assortments/bulk-order`, dto, {}, loaderId);
  }

  saveStockLimitProductPalette(loaderId: string, stockLimitProductPalette: StockLimitProductPaletteVM[] | any[]) {
    let request: HttpBasicRequest[] = [];
    stockLimitProductPalette.forEach((item) => {

      const limit = new limitDTI({
        minimumQuantityForPreOrder: item.minimumQuantityForPreOrder,
        minimumQuantityForReOrder: item.minimumQuantityForReOrder,
        stockLimit: item.stockLimit,
      });

      const isDelete = item.isDeleted || (item.stockLimit === null && item.minimumQuantityForPreOrder === null && item.minimumQuantityForReOrder === null);

      if (isDelete && item.productPaletteLimitId) {
        request = request.concat({
          url: `${this._baseUrl}/products/${item.productId}/palettes/limits/${item.productPaletteLimitId}`,
          method: 'delete',
        });
      } else if (item.productPaletteLimitId) {
        request = request.concat({
          url: `${this._baseUrl}/products/${item.productId}/palettes/limits/${item.productPaletteLimitId}`,
          body: limit,
          method: 'put',
        });
      } else if (!isDelete) {
        request = request.concat({
          url: `${this._baseUrl}/products/${item.productId}/palettes/${item.palette.id}/limits`,
          body: limit,
          method: 'post',
        });
      }

    });

    return this._httpClient.forkJoin(request, loaderId);
  }

  saveVariant(loaderId: string, variant: VariantVM, cacheVariant: VariantVM, mediaFiles: File[]) {
    const result = new Subject();
    let requests: HttpBasicRequest[] = [];
    requests = requests.concat(ProductsMapper.tagAssociateRequest(variant.tags, cacheVariant.tags, variant.id, this._baseUrl, 'variants'));
    requests = requests.concat(ProductsMapper.tagDeleteRequest(variant.tags, cacheVariant.tags, variant.id, this._baseUrl, 'variants'));
    requests = requests.concat(ProductsMapper.tierAssociateRequest(variant.tiers, cacheVariant.tiers, variant.id, this._baseUrl, 'variants'));
    requests = requests.concat(ProductsMapper.tierDeleteRequest(variant.tiers, cacheVariant.tiers, variant.id, this._baseUrl, 'variants'));
    requests = requests.concat(ProductsMapper.variantEditRequest(ProductsMapper.VariantPutDTO(variant), ProductsMapper.VariantPutDTO(cacheVariant), this._baseUrl));
    requests = requests.concat(ProductsMapper.priceEditRequest(variant.id, variant.pricelists, cacheVariant.pricelists, variant, this._baseUrl));
    const mediaUploadRequest: HttpBasicRequest[] = MediagMapper.uploadMedia(mediaFiles, this._baseUrl,);
    let mediaUpdateRequest: HttpBasicRequest[] = [];

    const promise = new Promise((resolve) => {
      if (mediaUploadRequest.length > 0) {
        this._httpClient.forkJoin(mediaUploadRequest, loaderId).subscribe((res: MediaDTO[]) => {
          mediaUpdateRequest = MediagMapper.updateAssociatedMedia(res, variant.media, cacheVariant.media, this._baseUrl, variant.id, 'variants');
          resolve(mediaUpdateRequest);
        }, error => {
          console.error(error);
        });
      } else {
        mediaUpdateRequest = MediagMapper.updateAssociatedMedia(null, variant.media, cacheVariant.media, this._baseUrl, variant.id, 'variants');
        resolve(mediaUpdateRequest);
      }
    }).then(() => {
      requests = requests.concat(mediaUpdateRequest);
      this._httpClient.forkJoin(requests, loaderId).subscribe((res) => {
        result.next(res[0]);
      }, error => {
        console.error(error);
      });
    });

    return result;
  }


  saveAssortment(loaderId: string, assortment: AssortmentVM, cacheAssortment: AssortmentVM, mediaFiles: File[], varsIdToAdd: number[], varsIdToRemove: number[], rule: AssortmentRuleDTO[]): Observable<AssortmentVM> {
    const result = new Subject<AssortmentVM>();
    let requests: HttpBasicRequest[] = [];
    const assPromise = new Promise((resolve) => {
      if (!assortment.id) {
        this._httpClient.post(this._baseUrl + '/assortments', {
          storeId: this._shopContext.currentShop().id,
          channelId: assortment.channelSelected.id,
          title: assortment.title,
          type: assortment.type,
          description: assortment.description,
          visibleForReOrder: assortment.visibleForReOrder,
          visibleForPreOrder: assortment.visibleForPreOrder,
        }, null, loaderId).subscribe((res: AssortmentDTO) => {
          assortment.id = res.id;
          resolve(assortment);
        }, (err) => { console.error(err); });
      } else {
        const assReq = ProductsMapper.AssortmentEditRequest(assortment, cacheAssortment, this._baseUrl);
        if (assReq.length > 0) {
          this._httpClient.forkJoin(assReq, loaderId).subscribe((res) => {
            resolve(assortment);
          }, error => {
            console.error(error);
          });
        } else {
          resolve(assortment);
        }

      }
    }).then((ass: AssortmentVM) => {
      requests = requests.concat(ProductsMapper.tagAssociateRequest(ass.tags, cacheAssortment.tags, ass.id, this._baseUrl, 'assortments'));
      requests = requests.concat(ProductsMapper.tagDeleteRequest(ass.tags, cacheAssortment.tags, ass.id, this._baseUrl, 'assortments'));
      requests = requests.concat(ProductsMapper.tierAssociateRequest(ass.tiers, cacheAssortment.tiers, ass.id, this._baseUrl, 'assortments'));
      requests = requests.concat(ProductsMapper.tierDeleteRequest(ass.tiers, cacheAssortment.tiers, ass.id, this._baseUrl, 'assortments'));
      requests = requests.concat(ProductsMapper.ruleRequest(rule, ass.id, this._baseUrl));
      requests = requests.concat(ProductsMapper.removeVariantsRequest(varsIdToRemove, ass.id, this._baseUrl));
      requests = requests.concat(ProductsMapper.addVariantsRequest(varsIdToAdd, ass.id, this._baseUrl));
      const mediaUploadRequest: HttpBasicRequest[] = MediagMapper.uploadMedia(mediaFiles, this._baseUrl,);
      let mediaUpdateRequest: HttpBasicRequest[] = [];
      const promise = new Promise((resolve) => {
        if (mediaUploadRequest.length > 0) {
          this._httpClient.forkJoin(mediaUploadRequest, loaderId).subscribe((res: MediaDTO[]) => {
            mediaUpdateRequest = MediagMapper.updateAssociatedMedia(res, ass.media, cacheAssortment.media, this._baseUrl, ass.id, 'assortments');
            resolve(mediaUpdateRequest);
          }, error => {
            console.error(error);
          });
        } else {
          mediaUpdateRequest = MediagMapper.updateAssociatedMedia(null, ass.media, cacheAssortment.media, this._baseUrl, ass.id, 'assortments');
          resolve(mediaUpdateRequest);
        }
      }).then(() => {
        requests = requests.concat(mediaUpdateRequest);
        if (requests.length) {
          this._httpClient.forkJoin(requests, loaderId).subscribe((res) => {
            if (assortment.type !== IncludeModeEnum.static) {
              this._httpClient.post(`${this._baseUrl}/assortments/${ass.id}/refresh-rules`, null, null, loaderId).subscribe((res: any) => {
                result.next(ass);
              });
            } else {
              result.next(ass);
            }
          }, error => {
            console.error(error);
          });
        } else {
          result.next(ass);
        }
      });
    });

    return result.asObservable();
  }



  public getBulkVariantsDetails(loaderId: string, variantIds: number[]): Observable<BulkEditorVariantVM[]> {
    let url = this._baseUrl + '/variants/variants-bulk-list';
    return this._httpClient.post<BulkEditorVariantDTO[]>(url, variantIds, null, loaderId).pipe(map((data: BulkEditorVariantDTO[]) => data.map(x => ProductsMapper.BulkEditorVariantVM(x))));
  }

  public updateBulkVariants(loaderId: string, variants: BulkEditorVariantVM[]): Observable<VariantBulkEditorDto[]> {
    let url = this._baseUrl + '/variants/bulk-edit';

    const variantsForUpdate: BulkEditorVariantDTI[] = UtilityFunctions.DeepClone(variants).map(variant => {
      return new BulkEditorVariantDTI({
        blankCellsDeliveryDate: variant.blankCellsDeliveryDate,
        blankCellsEndDate: variant.blankCellsEndDate,
        blockQuantityBasedReOrderVisibility: variant.blockQuantityBasedReOrderVisibility,
        cost: variant.cost,
        id: variant.id,
        overrideWholesaleDiscountPercentage: variant.overrideWholesaleDiscountPercentage,
        retailPriceForPreOrder: variant.retailPriceForPreOrder,
        retailPriceForReOrder: variant.retailPriceForReOrder,
        tagList: variant.tagList.map(t => t.id),
        tierList: variant.tierList.map(t => t.id),
        visibleForPreOrder: variant.visibleForPreOrder,
        visibleForReOrder: variant.visibleForReOrder,
      });
    });
    return this._httpClient.put<VariantBulkEditorDto[]>(url, variantsForUpdate, {}, loaderId);
  }

  removeAssortment(loaderId: string, assortmentId: number) {
    let url = this._baseUrl + `/assortments/${assortmentId}`;
    return this._httpClient.delete(url, {}, loaderId);
  }

  getSize(query: GetPagedSearchParams) {
    const { loaderId, skipCount, maxResultCount, search: searchValue = null } = query;
    const params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: searchValue ?? '',
      storeId: this._shopContext.currentShop().id,
    };
    return this._httpClient.get<PagedResponse<SizeDTO>>(this._baseUrl + `/sizes`, { params }, loaderId);
  }

  getPalette(query: GetPagedSearchParams) {
    const { loaderId, skipCount, maxResultCount, search: searchValue = null } = query;
    const params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: searchValue ?? '',
      sorting: "externalColorCode, description",
    };
    return this._httpClient.get<PagedResponse<VariantPaletteDTO>>(this._baseUrl + `/palettes`, { params }, loaderId);
  }

  getJobDetails(loaderId: string, externalId: string): Observable<JobDetailsVm> {
    const params = { externalId, skipCount: 0, maxResultCount: 1000 };
    const _sub = new Subject<JobDetailsVm>();
    this._httpClient.get<PagedResponse<JobDetailsDto>>(this._integrationBaseUrl + `/job-detail`, { params }, loaderId).subscribe(data => {
      let jobDetail: JobDetailsDto = null;
      let result: JobDetailsVm = null;
      data?.items?.filter(x => x.storedId === this._shopContext.currentShop()?.id).forEach(x => {
        if (!jobDetail || jobDetail.startTime < x.startTime) jobDetail = x;
      });

      if (jobDetail?.requestedByUserId)
        this.getUserName(jobDetail.requestedByUserId, loaderId).subscribe(user => {
          result = { ...jobDetail, user: user };
          _sub.next(result);
        });
      else {
        result = { ...jobDetail, user: null };
        _sub.next(result);
      }
    });
    return _sub.asObservable();
  }

  getUserName(id: number, loaderId: string): Observable<UserDTO> {
    return this._httpClient.get<UserDTO>(`${this._baseUrl}/users/${id}/info`, null, loaderId);
  }

  changeBulkMediaVisibility(loaderId: string, show: boolean, products: ProductDTO[]) {
    const request: HttpBasicRequest[] = [];
    const result = new Subject<any>();
    products.forEach(product => {
      product.hideAllMedia = !show;
      request.push({ url: `${this._baseUrl}/products/${product.id}`, method: 'put', body: product });
    });
    this._httpClient.forkJoin(request, loaderId).subscribe(res => {
      result.next(res);
    });
    return result;
  }

  getStockLimitProductPalettes(loaderId: string, productId: number) {
    return this._httpClient.get<ProductPaletteLimitDTO[]>(`${this._baseUrl}/products/${productId}/palettes`, {}, loaderId);
  }

  unpublishAllProducts(loaderId: string) {
    return this._httpClient.post(`${this._baseUrl}/variants/store/${this._shopContext.currentShop().id}/hide-all-for-pre-order`, {}, {}, loaderId);
  }

  getDistinctPaletteByProductId(productId: number, loaderId: string) {
    return this._httpClient.get<PaletteDTO[]>(`${this._baseUrl}/products/${productId}/distinct-palettes`, {}, loaderId).pipe(map(data => data.map(x => new PaletteVM(x))));
  }

  getProductIsCustomized(productId: number, loaderId: string) {
    return this._httpClient.get<PaletteDTO>(`${this._baseUrl}/products/${productId}/customizable`, {}, loaderId).pipe(map(data => new PaletteVM(data)));
  }

  getCustomizableProductList(query: GetCustomizableProductListParams) {
    const { loaderId, skipCount, maxResultCount, searchString, channelId, posId } = query;
    const params: Params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: searchString || '',
      channelId: channelId.toString(),
      posId: posId.toString(),
      promoOnly: 'false',
      mode: '0',
      storeId: this._shopContext.currentShop().id.toString(),
      isCustomizable: 'true',
    };

    return this._httpClient.get<PagedResponse<ProductOverview>>(`${this._baseUrl}/products-list`, { params }, loaderId).pipe(
      map(data => new PagedResponse({
        items: data.items.map(x => new ProductOverview(x)),
        totalCount: data.totalCount
      }))
    );
  }

  getProductPriceData(productId: number, mode: OrderTypeEnum, channelId: number): Observable<ProductPriceDetails> {
      return this._httpClient.get<ProductPriceDetails>(`${this._baseUrl}/products/${productId}/price-details`, {
        params: UtilityFunctions.prepareParams({
          mode: mode?.toString(),
          channelId
        })
      }).pipe(
        catchError(error => {
          console.error(error);
          return of(null);
        }),
        map(x => new ProductPriceDetails(x))
      );
    }

  getProductCardByProductId(productId: number) {
      return this._httpClient.get<ProductCard>(`${this._baseUrl}/products/${productId}`).pipe(
        map(x => new ProductCard(x))
      );
    }
}
